import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ChoiceApi, FlashcardApi, GapFillApi, ReorderApi, SayItBackApi } from '../api';
import { ExerciseElement } from '../base';
import { currentTarget, t, targetDir, targetLangAttr } from '../../i18n';
import { grade, gradeOptionsFor, spikesFor, type Miss } from '../../grade.generated';
import { iso, tPart, ui, uiDir } from '../../bidi-html';
import { signHostOf, signZone } from '../sign';
import { giveUpButton, giveUpOffered } from '../give-up';
import { compareAnswerPhrases } from '../answer-comparison';

// ---- Flashcard --------------------------------------------------------------


@customElement('li-flashcard')
export class LiFlashcard extends ExerciseElement<typeof FlashcardApi> {
  protected readonly api = FlashcardApi;
  @state() private flipped = false;
  @state() private graded: boolean | null = null;
  @state() private armed = false;
  private armTimer: ReturnType<typeof setTimeout> | undefined;
  protected resetState() { this.clearArm(); this.flipped = false; this.graded = null; this.armed = false; }

  disconnectedCallback() {
    this.clearArm();
    super.disconnectedCallback();
  }

  private clearArm() {
    if (this.armTimer !== undefined) clearTimeout(this.armTimer);
    this.armTimer = undefined;
  }

  render() {
    const p = this.controller?.props;
    if (!p) return nothing;
    const locked = this.graded !== null;
    // Script-illiterate learners read the Latin transliteration big; the
    // script stays alongside (small) for gradual exposure.
    const front = p.latin
      ? html`<div class="big-ca" dir="auto">${p.latin}</div><div class="ca muted small" dir="auto">${p.ca}</div>`
      : html`<div class="big-ca" dir="auto">${p.ca}</div>`;
    return html`
      <div class="ex flashcard" @click=${this.flip} role="button" tabindex="0" @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.flip()}>
        <div class="label">${t('ex.flashcard')}</div>
        ${this.flipped
          ? html`
              <div class="big-ca" style="font-size:20px" dir=${uiDir(p.en)}>${iso(p.en)}</div>
              ${p.note ? html`<div class="note" dir=${uiDir(p.note)}>${iso(p.note)}</div>` : nothing}
              ${p.example
                ? p.exampleLatin
                  ? html`<div class="note ca" dir="auto">“${p.exampleLatin}”</div><div class="ca muted small" dir="auto">${p.example}</div>`
                  : html`<div class="note ca" dir="auto">“${p.example}”</div>`
                : nothing}
              <div class="grade-col" @click=${(e: Event) => e.stopPropagation()}>
                <button class="btn grade-missed ${this.graded === false ? 'chosen' : ''}" ?disabled=${!this.armed || locked} @click=${() => this.grade(false)}>${t('ex.missed')}</button>
                <button class="btn grade-got ${this.graded === true ? 'chosen' : ''}" ?disabled=${!this.armed || locked} @click=${() => this.grade(true)}>${t('ex.gotIt')}</button>
              </div>
            `
          : front}
        <div class="hint">${this.flipped ? (locked ? t('ex.tapFront') : this.armed ? t('ex.gradeSelf') : '…') : t('ex.tapReveal')}</div>
      </div>
    `;
  }

  private flip = () => {
    this.flipped = !this.flipped;
    if (this.flipped) {
      this.armed = false;
      this.clearArm();
      // Arm the grade buttons after the flip settles: the second tap of a
      // double-tap meant to reveal must never land as a grade.
      this.armTimer = setTimeout(() => { this.armed = true; }, 350);
      this.send('flipped', { ca: this.controller?.props?.ca });
    } else {
      this.clearArm();
      this.armed = false;
    }
  };

  private grade(correct: boolean) {
    if (!this.armed || this.graded !== null) return;
    this.graded = correct;
    const p = this.controller?.props;
    this.send('grade', { ca: p?.ca, correct, expected: p?.en });
  }
}
export const Flashcard = { ...FlashcardApi, tagName: 'li-flashcard' };

// ---- GapFill ----------------------------------------------------------------

/** First sound of the answer's first word for tier-1 hints (matches server/src/learn.ts firstSound). Handles Catalan initial digraphs (qu/gu before e/i, ll, ny, ch) and skips Spanish opening marks (¿ ¡); other scripts fall back to the first character. */
export function firstSound(word: string): string {
  const t = word.trim().replace(/^[¿¡]+/, '');
  const w = t.toLowerCase();
  if ((w.startsWith('qu') || w.startsWith('gu')) && 'eéií'.includes(w[2] ?? '')) return t.slice(0, 2);
  for (const d of ['ll', 'ny', 'ch']) if (w.startsWith(d)) return t.slice(0, 2);
  return t.slice(0, 1);
}

/**
 * Direction and `lang` for a typed answer box follow the script the learner
 * must type, not the target language: Finglish (or any Latin answer) is typed
 * left-to-right even for a Persian target; only a Persian/Arabic-script answer
 * gets right-to-left. `dir="auto"` can't decide on an empty box.
 */
export function answerInput(answer: string): { dir: 'rtl' | 'ltr' | 'auto'; lang: string | undefined } {
  const lang = targetLangAttr();
  if (/[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/.test(answer)) return { dir: 'rtl', lang };
  if (/[A-Za-z\u00C0-\u024F]/.test(answer)) return { dir: 'ltr', lang: lang && targetDir() === 'rtl' ? `${lang}-Latn` : lang };
  return { dir: targetDir(), lang };
}

/**
 * The expected answer with each missed spike marked (ticket 05), isolated in
 * the answer's own direction so it reads right inside a Persian interface. A
 * spike typed where the answer has none shows struck through at its spot.
 */
export function markedAnswer(expected: string, missed: readonly Miss[]) {
  const input = answerInput(expected);
  const parts: unknown[] = [];
  let at = 0;
  for (const m of [...missed].sort((a, b) => a.at - b.at)) {
    if (m.at < at) continue;
    parts.push(expected.slice(at, m.at));
    const name = t(`spike.${m.spike}`);
    parts.push(m.length
      ? html`<mark class="spike" title=${name}>${expected.slice(m.at, m.at + m.length)}</mark>`
      : html`<mark class="spike extra" title=${name}><s>${m.got}</s></mark>`);
    at = m.at + m.length;
  }
  parts.push(expected.slice(at));
  return html`<bdi class="ca spike-answer" dir=${input.dir === 'rtl' ? 'rtl' : 'ltr'} lang=${input.lang ?? nothing}>${parts}</bdi>`;
}

/**
 * An answer the learner asked to see (Show me, ticket 32), isolated in its
 * own direction like `markedAnswer`, so it reads right in a Persian interface.
 */
export function shownAnswer(answer: string) {
  const input = answerInput(answer);
  return html`<bdi class="ca shown-answer" dir=${input.dir === 'rtl' ? 'rtl' : 'ltr'} lang=${input.lang ?? nothing}>${answer}</bdi>`;
}

/** The original attempt stays on the card; this sheet compares expected phrases underneath. */
export function revealedAnswer(given: string, expected: string) {
  const input = answerInput(expected);
  const comparison = compareAnswerPhrases(given, expected);
  const phrases = comparison.parts.filter((part) => part.expected);
  const removed = comparison.parts.filter((part) => !part.expected && part.given);
  return html`<div class="result shown">
    <div class="comparison-sheet" dir=${ui()}>
      <div class="comparison-label">${t('ex.expectedAnswer')}</div>
      <span class="sr-only">${shownAnswer(expected)}</span>
      <bdi class="comparison-line" dir=${input.dir === 'rtl' ? 'rtl' : 'ltr'} lang=${input.lang ?? nothing} aria-hidden="true">
        ${phrases.map((phrase, i) => html`${i ? ' ' : ''}${phrase.unchanged
          ? phrase.expected
          : html`<mark class="comparison-phrase">${phrase.expected}</mark>`}`)}
      </bdi>
      ${removed.length ? html`<div class="comparison-removal" dir=${ui()}>
        ${t('ex.leaveOut')} ${removed.map((phrase, i) => html`${i ? t('common.dot') : ''}<del><bdi dir="auto">${phrase.given}</bdi></del>`)}
      </div>` : nothing}
      ${comparison.orderChanged ? html`<div class="comparison-note" dir=${ui()}>${t('ex.wordOrderChanged')}</div>` : nothing}
    </div>
  </div>`;
}

@customElement('li-gapfill')
export class LiGapFill extends ExerciseElement<typeof GapFillApi> {
  protected readonly api = GapFillApi;
  @state() private value = '';
  /**
   * `scratch`: passed with a spike missed (ticket 05). On a spelling card a scratch is `bad`.
   * `shown`: the learner asked to see the answer (Show me).
   */
  @state() private result: 'ok' | 'scratch' | 'bad' | 'shown' | null = null;
  @state() private scratch: { expected: string; missed: Miss[] } | null = null;
  @state() private hints: string[] = [];
  @state() private disputed = false;
  protected resetState() { this.value = ''; this.result = null; this.scratch = null; this.hints = []; this.disputed = false; }

  /** Rescue tiers: first sound (always), then the pack hint (when present). Capped — hints never affect scoring. */
  private tiers(): string[] {
    const p = this.controller?.props;
    if (!p) return [];
    const first = p.answer.split(/\s+/)[0] ?? p.answer;
    const out = [t('ex.startsWith', { s: firstSound(first) })];
    if (p.hint) out.push(p.hint);
    return out;
  }

  render() {
    const p = this.controller?.props;
    if (!p) return nothing;
    const [before, after = ''] = p.sentence.split('___');
    const scratched = this.result === 'scratch' && this.scratch;
    const shown = scratched ? this.scratch!.expected : this.result === 'ok' ? p.answer : this.value || '…';
    const tiers = this.tiers();
    const left = tiers.length - this.hints.length;
    const input = answerInput(p.answer);
    const label = p.spelling?.length
      ? t('ex.spelling', { spikes: p.spelling.map((s) => t(`spike.${s}`)).join(' · ') })
      : t('ex.gapfill');
    const sign = signHostOf(this);
    const sentence = html`<div class="gap-sentence" dir=${uiDir(before + after)}>${iso(before)}<span class="gap-slot" dir=${input.dir === 'rtl' ? 'rtl' : 'ltr'} lang=${input.lang ?? nothing}>${shown}</span>${iso(after)}</div>`;
    const translation = p.translation ? html`<div class="note" dir=${uiDir(p.translation)}>${iso(p.translation)}</div>` : nothing;
    const hints = this.hints.length ? html`<div class="note" dir=${ui()}>💡 ${iso(this.hints.join(t('common.dot')))}</div>` : nothing;
    // Hanging as a sign, Show me waits in the thumb zone's foot once a miss has brought it there.
    // Before any try, a scored card offers "I don't know" in Show me's place (ticket 46); a spelling card keeps Show me.
    const giveUp = this.result === null && !p.spelling?.length && giveUpOffered(this);
    const ownShowMe = !giveUp && !(sign && this.result === 'bad');
    const tail = scratched
      ? html`<div class="result ok scratch" dir=${ui()}>${t('ex.scratch')} ${markedAnswer(this.scratch!.expected, this.scratch!.missed)}</div>`
      : this.result === 'ok'
      ? html`<div class="result ok" dir=${ui()}>${tPart('ex.correctIs', 'answer', shownAnswer(p.answer))}</div>`
      : this.result === 'shown'
      ? revealedAnswer(this.value, p.answer)
      : html`
          <input
            class="gap-input"
            type="text"
            autocapitalize="off"
            autocomplete="off"
            dir=${input.dir}
            lang=${input.lang ?? nothing}
            placeholder=${t('ex.typeWord')}
            .value=${this.value}
            @input=${(e: Event) => (this.value = (e.target as HTMLInputElement).value)}
            @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.check()}
          />
          <div class="actions">
            <button class="btn primary" @click=${this.check} ?disabled=${!this.value.trim()}>${t('ex.check')}</button>
            ${left > 0 ? html`<button class="btn" @click=${this.takeHint}>${left > 1 ? t('ex.hintN', { n: left }) : t('ex.hint')}</button>` : nothing}
            ${ownShowMe ? html`<button class="btn ghost" @click=${this.showAnswer}>${t('ex.showMe')}</button>` : nothing}
          </div>
          ${giveUp ? giveUpButton(this.giveUp) : nothing}
          ${this.result === 'bad' && this.scratch
            ? html`<div class="result bad spelling-missed" dir=${ui()}>${t('ex.spellingMissed')} ${markedAnswer(this.scratch.expected, this.scratch.missed)}</div>`
            : this.result === 'bad'
            ? html`<div class="result bad">${t('ex.notQuiteHint')}</div>
                <div class="actions">
                  ${this.disputed
                    ? html`<span class="muted small">${t('ex.flagged')}</span>`
                    : html`<button class="btn ghost" @click=${this.dispute}>${t('ex.shouldCount')}</button>`}
                </div>`
            : nothing}
        `;
    if (sign) {
      // The sentence is the bubble; the question, the box and its buttons sit in the thumb zone.
      return html`
        <div class="ex gapfill sign ${p.spelling?.length ? 'spelling' : ''}">
          ${sentence}${translation}
          ${signZone(sign, html`${this.result ? nothing : html`<div class="sign-prompt sign-fade" dir=${ui()}>${p.spelling?.length ? label : t('sign.gap')}</div>`}${hints}${tail}`)}
        </div>`;
    }
    return html`
      <div class="ex gapfill ${p.spelling?.length ? 'spelling' : ''}">
        <div class="label">${label}</div>
        ${sentence}${translation}${hints}${tail}
      </div>
    `;
  }

  private takeHint = () => {
    const tiers = this.tiers();
    if (this.hints.length >= tiers.length) return;
    this.hints = [...this.hints, tiers[this.hints.length]];
    this.send('hint', { tier: this.hints.length });
  };

  private check = () => {
    const p = this.controller?.props;
    if (!p) return;
    // The spelling grader (ticket 05), the same one the server runs: a missed
    // spike is a scratch, a pass on every card but a spelling card.
    const v = grade(this.value, [p.answer, ...(p.accept ?? []).map((a) => String(a))], spikesFor(currentTarget()), gradeOptionsFor(currentTarget()));
    const correct = v.kind === 'right' || (v.kind === 'scratched' && !p.spelling?.length);
    this.scratch = v.kind === 'scratched' ? { expected: v.expected, missed: v.missed } : null;
    this.result = v.kind === 'right' ? 'ok' : correct ? 'scratch' : 'bad';
    this.send('answer', {
      value: this.value, correct, expected: p.answer, hintsUsed: this.hints.length,
      ...(v.kind === 'scratched' ? { scratched: true, missed: v.missed } : {}),
    });
  };

  private dispute = () => {
    const p = this.controller?.props;
    if (!p || this.disputed) return;
    this.disputed = true;
    this.send('dispute', { value: this.value, expected: p.answer, hintsUsed: this.hints.length });
  };

  /** "I don't know — show me" (ticket 46): the answer shows and an ordinary wrong answer is sent, with no text. */
  private giveUp = () => {
    const p = this.controller?.props;
    if (!p || this.result !== null) return;
    this.result = 'shown';
    this.scratch = null;
    this.send('answer', { value: '', correct: false, gaveUp: true, expected: p.answer, hintsUsed: this.hints.length });
  };

  /** Show the answer: the card's own Show me, and the lesson bar's after a miss (ticket 32). */
  showAnswer = () => {
    const p = this.controller?.props;
    if (!p) return;
    this.result = 'shown';
    this.scratch = null;
    this.send('answer', { value: p.answer, correct: false, revealed: true, expected: p.answer, hintsUsed: this.hints.length });
  };
}
export const GapFill = { ...GapFillApi, tagName: 'li-gapfill' };

// ---- Choice -----------------------------------------------------------------


@customElement('li-choice')
export class LiChoice extends ExerciseElement<typeof ChoiceApi> {
  protected readonly api = ChoiceApi;
  @state() private picked: number | null = null;
  protected resetState() { this.picked = null; }

  render() {
    const p = this.controller?.props;
    if (!p) return nothing;
    const done = this.picked !== null;
    const sign = signHostOf(this);
    const result = done
      ? html`<div class="result ${this.picked === p.correct ? 'ok' : 'bad'}">
          <span dir=${ui()}>${this.picked === p.correct ? t('ex.correct') : tPart('ex.answerWas', 'a', html`<bdi dir="auto">${p.options[p.correct]}</bdi>`)}</span>
          ${p.explain && !sign ? html`<div class="note" dir=${uiDir(p.explain)}>${iso(p.explain)}</div>` : nothing}
        </div>`
      : nothing;
    const giveUp = !done && giveUpOffered(this) ? giveUpButton(this.giveUp) : nothing;
    if (sign) {
      // Hanging as a sign: the question is the bubble, the options (then the result) sit in the thumb zone, and the
      // explanation joins the bubble once answered.
      const beat = done
        ? html`<div class="sign-fade">${result}</div>`
        : html`${sign.heardOnly?.() ? html`<div class="sign-prompt sign-fade" dir=${ui()}>${t('sign.listen')}</div>` : nothing}<div class="options">
            ${p.options.map((o, i) => html`<button class="option" @click=${() => this.pick(i)} dir="auto">${o}</button>`)}
          </div>${giveUp}`;
      return html`
        <div class="ex choice sign">
          <div class="sign-stem" dir=${uiDir(p.question)}>${iso(p.question)}</div>
          ${done && p.explain ? html`<div class="sign-translation sign-fade" dir=${uiDir(p.explain)}>${iso(p.explain)}</div>` : nothing}
          ${signZone(sign, beat)}
        </div>`;
    }
    return html`
      <div class="ex choice">
        <div class="label">${t('ex.choose')}</div>
        <div class="big-ca" style="font-size:20px" dir=${uiDir(p.question)}>${iso(p.question)}</div>
        <div class="options">
          ${p.options.map(
            (o, i) => html`
              <button
                class="option ${done && i === p.correct ? 'correct' : ''} ${done && i === this.picked && i !== p.correct ? 'wrong' : ''}"
                ?disabled=${done}
                @click=${() => this.pick(i)}
                dir="auto"
              >
                ${o}
              </button>
            `,
          )}
        </div>
        ${giveUp}${result}
      </div>
    `;
  }

  private pick(i: number) {
    const p = this.controller?.props;
    if (!p || this.picked !== null) return;
    this.picked = i;
    this.send('answer', { value: p.options[i], correct: i === p.correct, expected: p.options[p.correct] });
  }

  /** "I don't know — show me" (ticket 46): no option is picked (-1), the right one is marked, a wrong answer is sent. */
  private giveUp = () => {
    const p = this.controller?.props;
    if (!p || this.picked !== null) return;
    this.picked = -1;
    this.send('answer', { value: '', correct: false, gaveUp: true, expected: p.options[p.correct] });
  };
}
export const Choice = { ...ChoiceApi, tagName: 'li-choice' };

// ---- Reorder ----------------------------------------------------------------


@customElement('li-reorder')
export class LiReorder extends ExerciseElement<typeof ReorderApi> {
  protected readonly api = ReorderApi;
  @state() private bank: number[] = [];
  @state() private line: number[] = [];
  /** `shown`: the learner asked to see the answer (Show me). */
  @state() private result: 'ok' | 'bad' | 'shown' | null = null;
  /** A try was checked: the card is answered, so "I don't know" is gone (Show me stays, unscored). */
  @state() private tried = false;
  private seededFor = '';

  private seed() {
    const p = this.controller?.props;
    if (!p) return;
    const key = p.words.join('');
    if (key === this.seededFor) return;
    this.seededFor = key;
    const idx = p.words.map((_, i) => i);
    // Fisher–Yates; re-shuffle if we accidentally produce the solved order.
    do {
      for (let i = idx.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [idx[i], idx[j]] = [idx[j], idx[i]]; }
    } while (idx.length > 1 && idx.every((v, i) => v === i));
    this.bank = idx; this.line = []; this.result = null; this.tried = false;
  }

  render() {
    const p = this.controller?.props;
    if (!p) return nothing;
    this.seed();
    // The sentence builds the way its script reads (an English one left to right in the Persian interface).
    const line = answerInput(p.words.join(' '));
    const sign = signHostOf(this);
    const translation = p.translation ? html`<div class="note" style="margin:0 0 10px" dir=${uiDir(p.translation)}>${iso(p.translation)}</div>` : nothing;
    const built = html`<div class="word-line" dir=${line.dir} lang=${line.lang ?? nothing}>
        ${this.line.map((i) => html`<button class="word placed" dir="auto" @click=${() => this.unplace(i)}>${p.words[i]}</button>`)}
      </div>`;
    const bank = html`<div class="word-bank" dir=${line.dir} lang=${line.lang ?? nothing}>
        ${this.bank.map((i) => html`<button class="word" dir="auto" @click=${() => this.place(i)}>${p.words[i]}</button>`)}
      </div>`;
    // Hanging as a sign, Show me waits in the thumb zone's foot once a miss has brought it there.
    const giveUp = this.result === null && !this.tried && giveUpOffered(this);
    const ownShowMe = !giveUp && !(sign && this.result === 'bad');
    const tail = this.result === 'ok'
      ? html`<div class="result ok" dir=${ui()}>${t('ex.correct')} — ${shownAnswer(p.words.join(' '))}</div>`
      : this.result === 'shown'
      ? html`<div class="result shown" dir=${ui()}>${t('ex.answerIs')} ${shownAnswer(p.words.join(' '))}</div>`
      : html`
          <div class="actions">
            <button class="btn primary" ?disabled=${this.bank.length > 0} @click=${this.check}>${t('ex.check')}</button>
            ${ownShowMe ? html`<button class="btn ghost" @click=${this.showAnswer}>${t('ex.showMe')}</button>` : nothing}
          </div>
          ${giveUp ? giveUpButton(this.giveUp) : nothing}
          ${this.result === 'bad' ? html`<div class="result bad">${t('ex.notQuiteReorder')}</div>` : nothing}
        `;
    if (sign) {
      // The meaning and the sentence being built are the bubble; the tiles and the buttons sit in the thumb zone.
      return html`
        <div class="ex reorder sign">
          ${translation}${built}
          ${signZone(sign, html`${this.result === 'ok' || this.result === 'shown' ? nothing : html`<div class="sign-prompt sign-fade" dir=${ui()}>${t('ex.reorder')}</div>${bank}`}${tail}`)}
        </div>`;
    }
    return html`
      <div class="ex reorder">
        <div class="label">${t('ex.reorder')}</div>
        ${translation}${built}${bank}${tail}
      </div>
    `;
  }

  private place(i: number) { this.bank = this.bank.filter((x) => x !== i); this.line = [...this.line, i]; this.result = null; }
  private unplace(i: number) { if (this.result === 'ok' || this.result === 'shown') return; this.line = this.line.filter((x) => x !== i); this.bank = [...this.bank, i]; this.result = null; }

  private check = () => {
    const p = this.controller?.props;
    if (!p) return;
    const correct = this.line.every((v, i) => v === i);
    this.tried = true;
    this.result = correct ? 'ok' : 'bad';
    this.send('answer', { value: this.line.map((i) => p.words[i]).join(' '), correct, expected: p.words.join(' ') });
  };

  /** "I don't know — show me" (ticket 46): the sentence builds itself and an ordinary wrong answer is sent. */
  private giveUp = () => {
    const p = this.controller?.props;
    if (!p || this.result !== null || this.tried) return;
    this.line = p.words.map((_, i) => i); this.bank = []; this.result = 'shown';
    this.send('answer', { value: '', correct: false, gaveUp: true, expected: p.words.join(' ') });
  };

  /** Show the answer: the card's own Show me, and the lesson bar's after a miss (ticket 32). */
  showAnswer = () => {
    const p = this.controller?.props;
    if (!p) return;
    this.line = p.words.map((_, i) => i); this.bank = []; this.result = 'shown';
    this.send('answer', { value: p.words.join(' '), correct: false, revealed: true, expected: p.words.join(' ') });
  };
}
export const Reorder = { ...ReorderApi, tagName: 'li-reorder' };

// ---- SayItBack --------------------------------------------------------------


@customElement('li-sayitback')
export class LiSayItBack extends ExerciseElement<typeof SayItBackApi> {
  protected readonly api = SayItBackApi;
  @state() private said = false;
  protected resetState() { this.said = false; }

  render() {
    const p = this.controller?.props;
    if (!p) return nothing;
    const sign = signHostOf(this);
    const line = p.latin
      ? html`<div class="big-ca" dir="auto">${p.latin}</div><div class="ca muted small" dir="auto">${p.ca}</div>`
      : html`<div class="big-ca" dir="auto">${p.ca}</div>`;
    const en = html`<div class="en" dir=${uiDir(p.en)}>${iso(p.en)}</div>`;
    const tip = p.tip ? html`<div class="note" dir=${uiDir(p.tip)}>${iso(p.tip)}</div>` : nothing;
    const button = html`<div class="actions">
        <button class="btn primary" ?disabled=${this.said} @click=${this.ready}>${this.said ? t('ex.listening') : t('ex.isaidit')}</button>
      </div>`;
    if (sign) {
      // The line to say is the bubble; the ask and its button sit in the thumb zone, the check under them.
      return html`
        <div class="ex sayit sign">
          ${line}${en}${tip}
          ${signZone(sign, html`<div class="sign-prompt" dir=${ui()}>${t('ex.sayit')}</div>${button}`)}
        </div>`;
    }
    return html`
      <div class="ex sayit">
        <div class="label">${t('ex.sayit')}</div>
        ${line}${en}${tip}${button}
      </div>
    `;
  }

  private ready = () => {
    this.said = true;
    this.send('ready', { ca: this.controller?.props?.ca });
  };
}
export const SayItBack = { ...SayItBackApi, tagName: 'li-sayitback' };
